import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { getExperienceOfferById } from '~/services/ExperiencesService';

import getDateDayDiff from '~/utils/getDateDayDiff';

import BaseOfferBox, { StatusBadge } from './BaseOfferBox';

interface Props {
  bk: string;
  index: number;
  onRemove: () => void;
  compact?: boolean;
}

type ExperienceOffer = NonNullable<Awaited<ReturnType<typeof getExperienceOfferById>>['result']>;
type StatusReason = NonNullable<ExperienceOffer['statusReasons']>[number];

function resolveStatusBadge(
  status: ExperienceOffer['status'],
  statusReasons: ExperienceOffer['statusReasons'],
): StatusBadge | undefined {
  if (status === 'ONLINE') return undefined;

  const reasons = statusReasons ?? [];

  if (reasons.includes('OFFER-EXPIRED' as StatusReason) || reasons.includes('BRAND-EXPIRED' as StatusReason)) {
    return { label: 'Expired', severity: 'error' };
  }
  if (reasons.includes('SCHEDULE-EXPIRED' as StatusReason)) {
    return { label: 'Ended', severity: 'error' };
  }
  if (reasons.includes('OFFLINE-OFFER' as StatusReason)) {
    return { label: 'Offline', severity: 'warning' };
  }
  if (reasons.includes('OFFER-DELETED' as StatusReason)) {
    return { label: 'Deleted', severity: 'error' };
  }

  return { label: 'Unavailable', severity: 'warning' };
}

function ExperienceOfferBox({ bk, index, onRemove, compact = false }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useSelector((state: App.State) => state.tenant);
  const [offer, setOffer] = useState<Awaited<ReturnType<typeof getExperienceOfferById>>['result']>();

  useEffect(() => {
    getExperienceOfferById({ id: bk, brand: tenant.brand, curationData: true })
      .then((res) => {
        setOffer(res.result);
      })
      .catch((err) => {
        if (err.status === 404) {
          onRemove && onRemove();
          return;
        }
        enqueueSnackbar(`Experience ${bk} could not be found`, { variant: 'error' });
      });
  }, [bk, tenant.brand]);

  if (!offer) {
    return <BaseOfferBox tenant={tenant} index={index} compact={compact} />;
  }

  return (
    <BaseOfferBox
      tenant={tenant}
      title={offer.title}
      imageId={offer.images[0]?.id}
      path={`/experience/${offer.slug}/${bk}`}
      statusBadge={resolveStatusBadge(offer.status, offer.statusReasons)}
      index={index}
      remainingDays={getDateDayDiff(offer.scheduledEndDate)}
      showOfflineDate
      offlineDate={offer.scheduledEndDate}
      compact={compact}
    />
  );
}

export default ExperienceOfferBox;
